<script setup lang="ts">
import { computed, onMounted, reactive, ref } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import CustomStore from 'devextreme/data/custom_store';
import type { LoadOptions } from 'devextreme/data';
import {
  DxDataGrid,
  DxColumn,
  DxScrolling,
  DxButton as DxGridButton,
  DxColumnChooser,
  DxStateStoring,
  DxToolbar,
  DxItem as DxGridToolbarItem,
  DxSelection,
} from 'devextreme-vue/data-grid';
import { DxButton } from 'devextreme-vue/button';
import { DxSelectBox } from 'devextreme-vue/select-box';
import { DxCheckBox } from 'devextreme-vue/check-box';
import { DxTextBox } from 'devextreme-vue/text-box';
import DxTextArea from 'devextreme-vue/text-area';
import { DxDateBox } from 'devextreme-vue/date-box';
import { DxLoadPanel } from 'devextreme-vue/load-panel';
import { DxTabPanel, DxItem } from 'devextreme-vue/tab-panel';
import { DxPopup, DxToolbarItem } from 'devextreme-vue/popup';
import { bottoGridPaging, showError, showSuccess } from '@botto/core';
import {
  botPoliciesApi,
  domainsApi,
  groupsApi,
  linksApi,
  targetsApi,
  type BotPolicy,
  type TrackerDomain,
  type TrackerGroup,
  type TrackerLink,
  type TargetListItem,
  type LinkStatsItem,
} from '../../api';
import DateRangePicker from '../../components/DateRangePicker.vue';
import StatusBadge from '../../components/StatusBadge.vue';
import TrackerListLayout from '../../components/TrackerListLayout.vue';
import {
  copyText,
  encodeTargetKey,
  formatDateTime,
  formatNumber,
  generateLinkCode,
  toApiDate,
  trackingUrl,
} from '../../utils/format';
import { pageFromLoadOptions, resetGridPage, trackerGridProps } from '../../utils/gridUi';

const router = useRouter();
const route = useRoute();
const { paging, pager } = bottoGridPaging({ pageSize: 50 });

const linksGrid = ref<InstanceType<typeof DxDataGrid> | null>(null);
const targetsGrid = ref<InstanceType<typeof DxDataGrid> | null>(null);
const mainTabIndex = ref(0);
const selectedLinkIds = ref<number[]>([]);
const selectedLinks = ref<TrackerLink[]>([]);
const copyingSelected = ref(false);
const copyProgress = ref<{ current: number; total: number } | null>(null);

const copyProgressMessage = computed(() => {
  if (!copyProgress.value) return 'Копирование…';
  return `Копирование: ${copyProgress.value.current} / ${copyProgress.value.total}`;
});

const domains = ref<TrackerDomain[]>([]);
const groups = ref<TrackerGroup[]>([]);
const botPolicies = ref<BotPolicy[]>([]);
const statsMap = ref<Record<number, LinkStatsItem>>({});

const FILTERS_STORAGE_KEY = 'botto.tracker.links.listFilters';
const GRID_STATE_KEY = 'botto.tracker.links.grid';

type StoredListFilters = {
  domain_id: number | null;
  group_id: number | null;
  q: string;
  from: string | null;
  to: string | null;
};

function queryParam(value: unknown): string {
  if (value == null) return '';
  const raw = Array.isArray(value) ? value[0] : value;
  return raw == null ? '' : String(raw);
}

function parseQueryId(value: unknown): number | null {
  const s = queryParam(value).trim();
  if (!s) return null;
  const n = Number(s);
  return Number.isFinite(n) ? n : null;
}

function readFiltersFromQuery(): Partial<StoredListFilters> | null {
  const q = route.query;
  const hasAny = ['domain_id', 'group_id', 'q', 'from', 'to'].some(
    (key) => queryParam(q[key]).trim() !== '',
  );
  if (!hasAny) return null;
  return {
    domain_id: parseQueryId(q.domain_id),
    group_id: parseQueryId(q.group_id),
    q: queryParam(q.q),
    from: queryParam(q.from) || null,
    to: queryParam(q.to) || null,
  };
}

function readStoredListFilters(): Partial<StoredListFilters> | null {
  try {
    const raw = localStorage.getItem(FILTERS_STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<StoredListFilters> & {
      name?: string;
      code?: string;
    };
    // Migrate legacy name/code filters into unified q.
    if (!parsed.q) {
      const legacy = [parsed.name, parsed.code].filter((s) => typeof s === 'string' && s.trim());
      if (legacy.length) parsed.q = String(legacy[0]).trim();
    }
    return parsed;
  } catch {
    return null;
  }
}

function parseStoredDate(value: string | null | undefined): Date | null {
  if (!value) return null;
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? null : d;
}

/** Prefer shareable URL query; fall back to localStorage / defaults. */
const initialFilters = readFiltersFromQuery() ?? readStoredListFilters();
// Date range filters links by created_at; default = all (null/null).
const rangeFrom = ref<Date | null>(parseStoredDate(initialFilters?.from));
const rangeTo = ref<Date | null>(parseStoredDate(initialFilters?.to));

/** List filters — read inside CustomStore.load (must stay in sync before refresh). */
const listFilters = reactive<{
  domain_id: number | null;
  group_id: number | null;
  q: string;
}>({
  domain_id:
    typeof initialFilters?.domain_id === 'number' ? initialFilters.domain_id : null,
  group_id: typeof initialFilters?.group_id === 'number' ? initialFilters.group_id : null,
  q: typeof initialFilters?.q === 'string' ? initialFilters.q : '',
});

function buildFilterQuery(): Record<string, string> {
  const query: Record<string, string> = {};
  if (listFilters.domain_id != null) query.domain_id = String(listFilters.domain_id);
  if (listFilters.group_id != null) query.group_id = String(listFilters.group_id);
  const q = listFilters.q.trim();
  if (q) query.q = q;
  const from = rangeFrom.value?.toISOString();
  const to = rangeTo.value?.toISOString();
  if (from) query.from = from;
  if (to) query.to = to;
  return query;
}

function syncFiltersToUrl() {
  const next = buildFilterQuery();
  const kept: Record<string, string> = {};
  for (const [key, value] of Object.entries(route.query)) {
    if (key === 'domain_id' || key === 'group_id' || key === 'q' || key === 'from' || key === 'to') {
      continue;
    }
    const s = queryParam(value).trim();
    if (s) kept[key] = s;
  }
  void router.replace({ query: { ...kept, ...next } });
}

function persistListFilters() {
  const payload: StoredListFilters = {
    domain_id: listFilters.domain_id,
    group_id: listFilters.group_id,
    q: listFilters.q,
    from: rangeFrom.value?.toISOString() ?? null,
    to: rangeTo.value?.toISOString() ?? null,
  };
  try {
    localStorage.setItem(FILTERS_STORAGE_KEY, JSON.stringify(payload));
  } catch {
    /* ignore quota / private mode */
  }
  syncFiltersToUrl();
}

async function copyFilterLink() {
  persistListFilters();
  const resolved = router.resolve({ path: route.path, query: buildFilterQuery() });
  const href = `${window.location.origin}${resolved.fullPath}`;
  try {
    await copyText(href);
    showSuccess('Ссылка на фильтр скопирована');
  } catch {
    showError(new Error('Не удалось скопировать ссылку'));
  }
}

const showModal = ref(false);
const editing = ref<TrackerLink | null>(null);
const saving = ref(false);
const formError = ref('');
const form = reactive({
  domain_id: null as number | null,
  group_id: null as number | null,
  name: '',
  target_url: '',
  code: '',
  status: 'active',
  expires_at: null as Date | null,
  challenge_on: false,
  bot_policy_id: null as number | null,
});

const botPolicyItems = computed(() => {
  const items = botPolicies.value.map((p) => ({
    id: p.id as number | null,
    name: p.is_system_default ? `${p.name} (system default)` : p.name,
  }));
  // Keep current value visible if list failed (non-admin) or policy was removed from catalog.
  const currentId = form.bot_policy_id;
  if (currentId != null && !items.some((i) => i.id === currentId)) {
    items.unshift({ id: currentId, name: `Policy #${currentId}` });
  }
  return items;
});

const statusItems = ['active', 'paused', 'expired'];

type BulkResult = {
  ok: boolean;
  url: string;
  trackingUrl?: string;
  error?: string;
};

const bulkProgress = ref<{ current: number; total: number } | null>(null);
const bulkResults = ref<BulkResult[]>([]);
const bulkDone = ref(false);
const createdLinksText = ref('');

const bulkTargetPlaceholder =
  'https://offer.example/land1\nhttps://offer.example/land2';

/** Domain picker label: `example.com (Node name)`. */
const domainSelectItems = computed(() =>
  domains.value.map((d) => ({
    ...d,
    label: d.tracker_node_name ? `${d.name} (${d.tracker_node_name})` : d.name,
  })),
);

const domainById = computed(() => {
  const m: Record<number, TrackerDomain> = {};
  for (const d of domains.value) m[d.id] = d;
  return m;
});

function parseTargetUrls(text: string): string[] {
  return text
    .split('\n')
    .map((line) => line.trim())
    .filter(Boolean);
}

const createTargetUrls = computed(() =>
  editing.value ? [] : parseTargetUrls(form.target_url),
);

const isBulkCreate = computed(() => !editing.value && createTargetUrls.value.length > 1);

function isValidHttpUrl(value: string): boolean {
  try {
    const u = new URL(value);
    return u.protocol === 'http:' || u.protocol === 'https:';
  } catch {
    return false;
  }
}

function defaultGroupId(): number | null {
  const def = groups.value.find((g) => g.name === 'Default');
  return def?.id ?? groups.value[0]?.id ?? null;
}

async function loadDomains() {
  const res = await domainsApi.listDomains({ page: 1, page_size: 200 });
  domains.value = res.items || [];
}

async function loadGroups() {
  const res = await groupsApi.listGroups({ page: 1, page_size: 200 });
  groups.value = res.items || [];
}

async function loadBotPolicies() {
  try {
    botPolicies.value = await botPoliciesApi.listBotPolicies();
  } catch {
    // Endpoint requires admin:all — non-admins keep empty list (system default only).
    botPolicies.value = [];
  }
}

async function loadStatsFor(items: TrackerLink[]) {
  if (!items.length) {
    statsMap.value = {};
    return;
  }
  try {
    const ids = items.map((l) => l.id).join(',');
    // List date range filters created_at only; click columns are all-time for visible rows.
    const res = await linksApi.linksStats({ link_ids: ids });
    const map: Record<number, LinkStatsItem> = {};
    for (const item of res.items || []) {
      map[item.link_id] = item;
    }
    statsMap.value = map;
  } catch (e) {
    showError(e);
  }
}

const linksStore = new CustomStore({
  key: 'id',
  loadMode: 'processed',
  async load(loadOptions: LoadOptions) {
    const { page, page_size } = pageFromLoadOptions(loadOptions, paging.pageSize);
    // Snapshot filters at call start — do not re-read after awaits.
    const domainId = listFilters.domain_id;
    const groupId = listFilters.group_id;
    const q = listFilters.q.trim();
    const createdFrom = toApiDate(rangeFrom.value);
    const createdTo = toApiDate(rangeTo.value);
    const params: {
      page: number;
      page_size: number;
      domain_id?: number;
      group_id?: number;
      q?: string;
      from?: string;
      to?: string;
    } = { page, page_size };
    if (domainId != null && Number.isFinite(domainId)) params.domain_id = Number(domainId);
    if (groupId != null && Number.isFinite(groupId)) params.group_id = Number(groupId);
    if (q) params.q = q;
    if (createdFrom) params.from = createdFrom;
    if (createdTo) params.to = createdTo;
    try {
      const res = await linksApi.listLinks(params);
      const items = res.items || [];
      await loadStatsFor(items);
      return { data: items, totalCount: res.total || 0 };
    } catch (e) {
      showError(e);
      return { data: [], totalCount: 0 };
    }
  },
});

const targetsStore = new CustomStore({
  key: 'target_url',
  loadMode: 'processed',
  async load(loadOptions: LoadOptions) {
    const { page, page_size } = pageFromLoadOptions(loadOptions, paging.pageSize);
    try {
      const res = await targetsApi.listTargets({
        from: toApiDate(rangeFrom.value),
        to: toApiDate(rangeTo.value),
        page,
        page_size,
      });
      return { data: res.items || [], totalCount: res.total || 0 };
    } catch (e) {
      showError(e);
      return { data: [], totalCount: 0 };
    }
  },
});

function refreshLinksGrid() {
  resetGridPage(linksGrid.value?.instance ?? null);
}

function refreshTargetsGrid() {
  resetGridPage(targetsGrid.value?.instance ?? null);
}

async function refresh() {
  try {
    if (mainTabIndex.value === 0) {
      await linksGrid.value?.instance?.getDataSource()?.reload();
    } else {
      await targetsGrid.value?.instance?.getDataSource()?.reload();
    }
  } catch (e) {
    showError(e);
  }
}

/** Links tab: range → created_at. Targets tab: range → click window for target stats. */
async function onRangeChange() {
  persistListFilters();
  if (mainTabIndex.value === 0) {
    refreshLinksGrid();
  } else {
    refreshTargetsGrid();
  }
}

async function onTabChanged(e: { component?: { option: (n: string) => number } }) {
  const idx = e.component?.option('selectedIndex') ?? mainTabIndex.value;
  mainTabIndex.value = idx;
  if (idx === 0) refreshLinksGrid();
  else refreshTargetsGrid();
}

/**
 * Domain/group filters → reload /links?domain_id=&group_id= (then /links/stats for visible rows).
 * Do not gate on e.event — DxSelectBox often omits it on user pick; skip only if value unchanged.
 */
function onDomainFilterChanged(e: { value?: number | null }) {
  const next = e.value ?? null;
  if (next === listFilters.domain_id) return;
  listFilters.domain_id = next;
  persistListFilters();
  refreshLinksGrid();
}

function onGroupFilterChanged(e: { value?: number | null }) {
  const next = e.value ?? null;
  if (next === listFilters.group_id) return;
  listFilters.group_id = next;
  persistListFilters();
  refreshLinksGrid();
}

let qFilterTimer: ReturnType<typeof setTimeout> | null = null;

function onQFilterChanged(e: { value?: string | null }) {
  const next = (e.value ?? '').trimStart();
  if (next === listFilters.q) return;
  listFilters.q = next;
  persistListFilters();
  if (qFilterTimer) clearTimeout(qFilterTimer);
  qFilterTimer = setTimeout(() => {
    qFilterTimer = null;
    refreshLinksGrid();
  }, 300);
}

function resetBulkState() {
  bulkProgress.value = null;
  bulkResults.value = [];
  bulkDone.value = false;
  createdLinksText.value = '';
}

function openCreate() {
  editing.value = null;
  form.domain_id = domains.value[0]?.id ?? null;
  form.group_id = defaultGroupId();
  form.name = '';
  form.target_url = '';
  form.code = generateLinkCode(3);
  form.status = 'active';
  form.expires_at = null;
  form.challenge_on = false;
  form.bot_policy_id = null;
  formError.value = '';
  resetBulkState();
  showModal.value = true;
}

function openEdit(link: TrackerLink) {
  editing.value = link;
  form.domain_id = link.domain_id;
  form.group_id = link.group_id ?? defaultGroupId();
  form.name = link.name || '';
  form.target_url = link.target_url;
  form.code = link.code;
  form.status = link.status;
  form.expires_at = link.expires_at ? new Date(link.expires_at) : null;
  form.challenge_on = link.challenge_mode === 'js';
  form.bot_policy_id = link.bot_policy_id ?? null;
  formError.value = '';
  resetBulkState();
  showModal.value = true;
}

function closeModal() {
  if (saving.value) return;
  showModal.value = false;
  resetBulkState();
}

/** Close after a successful save even if `saving` is still true. */
function closeModalAfterSave() {
  saving.value = false;
  showModal.value = false;
  resetBulkState();
}

function onPopupHiding(e: { cancel?: boolean }) {
  if (saving.value) {
    e.cancel = true;
    return;
  }
  resetBulkState();
}

async function copyCreatedLinks() {
  if (!createdLinksText.value.trim()) return;
  try {
    await copyText(createdLinksText.value);
    showSuccess('Ссылки скопированы');
  } catch {
    showError(new Error('Не удалось скопировать'));
  }
}

async function createOneLink(targetUrl: string): Promise<BulkResult> {
  const challenge_mode = form.challenge_on ? 'js' : 'off';
  const expires_at = form.expires_at ? toApiDate(form.expires_at) ?? null : null;
  const body: Parameters<typeof linksApi.createLink>[0] = {
    domain_id: form.domain_id!,
    name: form.name.trim() || null,
    target_url: targetUrl,
    status: form.status,
    challenge_mode,
    expires_at,
    bot_policy_id: form.bot_policy_id,
  };
  if (form.group_id) body.group_id = form.group_id;
  // Bulk: never send code — auto-generated per link. Single create may set code below.
  try {
    const created = await linksApi.createLink(body);
    const url =
      created.tracking_url ||
      trackingUrl(created.domain_name || domainById.value[created.domain_id]?.name, created.code) ||
      '';
    return { ok: true, url: targetUrl, trackingUrl: url || undefined };
  } catch (e) {
    return {
      ok: false,
      url: targetUrl,
      error: e instanceof Error ? e.message : 'Ошибка создания',
    };
  }
}

async function saveBulk(urls: string[]) {
  bulkProgress.value = { current: 0, total: urls.length };
  bulkResults.value = [];
  createdLinksText.value = '';
  const created: string[] = [];

  for (const url of urls) {
    const result = await createOneLink(url);
    bulkResults.value.push(result);
    if (result.ok && result.trackingUrl) created.push(result.trackingUrl);
    bulkProgress.value = {
      current: bulkProgress.value.current + 1,
      total: urls.length,
    };
  }

  createdLinksText.value = created.join('\n');
  const okCount = bulkResults.value.filter((r) => r.ok).length;
  const failCount = bulkResults.value.length - okCount;
  if (okCount > 0) {
    showSuccess(
      failCount
        ? `Создано ${okCount} из ${urls.length}, ошибок: ${failCount}`
        : `Создано ссылок: ${okCount}`,
    );
    if (created.length) {
      try {
        await copyText(created.join('\n'));
        showSuccess(
          created.length === 1 ? 'URL скопирован' : `Скопировано URL: ${created.length}`,
        );
      } catch {
        /* ignore */
      }
    }
    closeModalAfterSave();
    refreshLinksGrid();
  } else {
    bulkDone.value = true;
    formError.value = 'Не удалось создать ни одной ссылки';
  }
}

async function save() {
  saving.value = true;
  formError.value = '';
  try {
    const challenge_mode = form.challenge_on ? 'js' : 'off';
    const expires_at = form.expires_at ? toApiDate(form.expires_at) ?? null : null;
    if (editing.value) {
      const code = form.code.trim();
      if (!code) {
        formError.value = 'Укажите code';
        return;
      }
      if (!form.domain_id) {
        formError.value = 'Выберите домен';
        return;
      }
      const body: Parameters<typeof linksApi.updateLink>[1] = {
        domain_id: form.domain_id,
        name: form.name.trim() || null,
        code,
        target_url: form.target_url.trim(),
        status: form.status,
        challenge_mode,
        expires_at,
        bot_policy_id: form.bot_policy_id,
      };
      if (form.group_id) body.group_id = form.group_id;
      await linksApi.updateLink(editing.value.id, body);
      showSuccess('Ссылка обновлена');
      closeModalAfterSave();
      refreshLinksGrid();
      return;
    }

    if (!form.domain_id) {
      formError.value = 'Выберите домен';
      return;
    }
    const urls = parseTargetUrls(form.target_url);
    if (!urls.length) {
      formError.value = 'Укажите хотя бы одну исходную ссылку';
      return;
    }
    const invalid = urls.filter((u) => !isValidHttpUrl(u));
    if (invalid.length) {
      formError.value = `Некорректный URL: ${invalid[0]}`;
      return;
    }

    if (urls.length > 1) {
      await saveBulk(urls);
      return;
    }

    const body: Parameters<typeof linksApi.createLink>[0] = {
      domain_id: form.domain_id,
      name: form.name.trim() || null,
      target_url: urls[0],
      status: form.status,
      challenge_mode,
      expires_at,
      bot_policy_id: form.bot_policy_id,
    };
    if (form.group_id) body.group_id = form.group_id;
    if (form.code?.trim()) body.code = form.code.trim();
    const created = await linksApi.createLink(body);
    showSuccess('Ссылка создана');
    const url = created.tracking_url || trackingUrl(created.domain_name, created.code);
    if (url) {
      try {
        await copyText(url);
        showSuccess('URL скопирован');
      } catch {
        /* ignore */
      }
    }
    closeModalAfterSave();
    refreshLinksGrid();
  } catch (e) {
    formError.value = e instanceof Error ? e.message : 'Ошибка сохранения';
  } finally {
    saving.value = false;
  }
}

function fullTrackingUrl(link: TrackerLink): string {
  return (
    link.tracking_url ||
    trackingUrl(link.domain_name || domainById.value[link.domain_id]?.name, link.code) ||
    ''
  );
}

async function copyUrl(link: TrackerLink) {
  const url = fullTrackingUrl(link);
  try {
    await copyText(url);
    showSuccess('Скопировано');
  } catch {
    showError(new Error('Не удалось скопировать'));
  }
}

async function duplicateLink(link: TrackerLink) {
  try {
    const created = await linksApi.copyLink(link.id);
    showSuccess(`Создана копия: ${created.code}`);
    const url =
      created.tracking_url ||
      trackingUrl(created.domain_name || domainById.value[created.domain_id]?.name, created.code);
    if (url) {
      try {
        await copyText(url);
        showSuccess('URL копии скопирован');
      } catch {
        /* ignore */
      }
    }
    await linksGrid.value?.instance?.refresh();
  } catch (e) {
    showError(e);
  }
}

function onLinksSelectionChanged(e: {
  selectedRowKeys?: Array<number | string>;
  selectedRowsData?: TrackerLink[];
}) {
  selectedLinkIds.value = (e.selectedRowKeys ?? [])
    .map((k) => Number(k))
    .filter((n) => Number.isFinite(n));
  selectedLinks.value = e.selectedRowsData ?? [];
}

function clearLinkSelection() {
  selectedLinkIds.value = [];
  selectedLinks.value = [];
  linksGrid.value?.instance?.clearSelection();
}

async function duplicateSelected() {
  const links = [...selectedLinks.value];
  if (!links.length) return;
  if (!window.confirm(`Создать копии для ${links.length} ссылок?`)) return;

  copyingSelected.value = true;
  copyProgress.value = { current: 0, total: links.length };
  let ok = 0;
  let fail = 0;
  const createdUrls: string[] = [];

  for (const link of links) {
    try {
      const created = await linksApi.copyLink(link.id);
      ok += 1;
      const url =
        created.tracking_url ||
        trackingUrl(
          created.domain_name || domainById.value[created.domain_id]?.name,
          created.code,
        );
      if (url) createdUrls.push(url);
    } catch {
      fail += 1;
    }
    copyProgress.value = {
      current: (copyProgress.value?.current ?? 0) + 1,
      total: links.length,
    };
  }

  copyingSelected.value = false;
  copyProgress.value = null;
  clearLinkSelection();
  await linksGrid.value?.instance?.refresh();

  if (ok > 0) {
    showSuccess(fail ? `Скопировано ${ok} из ${links.length}, ошибок: ${fail}` : `Создано копий: ${ok}`);
    if (createdUrls.length) {
      try {
        await copyText(createdUrls.join('\n'));
        showSuccess('URL копий скопированы');
      } catch {
        /* ignore */
      }
    }
  } else {
    showError(new Error('Не удалось создать ни одной копии'));
  }
}

async function removeLink(link: TrackerLink) {
  if (!window.confirm(`Удалить ссылку ${link.code}? Это нельзя отменить.`)) return;
  try {
    await linksApi.deleteLink(link.id);
    showSuccess('Ссылка удалена');
    await linksGrid.value?.instance?.refresh();
  } catch (e) {
    showError(e);
  }
}

function goLinkDetail(link: TrackerLink) {
  router.push(`/tracker/links/${link.id}`);
}

function linkDetailHref(link: TrackerLink): string {
  return router.resolve(`/tracker/links/${link.id}`).href;
}

function goTargetDetail(t: TargetListItem) {
  router.push(`/tracker/links/targets/${encodeTargetKey(t.target_url)}`);
}

function statsOf(linkId: number, field: keyof LinkStatsItem): string {
  return formatNumber(statsMap.value[linkId]?.[field] as number | undefined);
}

onMounted(async () => {
  persistListFilters();
  try {
    await Promise.all([loadDomains(), loadGroups(), loadBotPolicies()]);
  } catch (e) {
    showError(e);
  }
});
</script>

<template>
  <TrackerListLayout title="Ссылки">
    <template #actions>
      <DxButton
        v-if="mainTabIndex === 0"
        icon="add"
        text="Создать ссылку"
        type="default"
        styling-mode="contained"
        @click="openCreate"
      />
      <DxButton
        v-if="mainTabIndex === 0"
        icon="copy"
        :text="
          selectedLinkIds.length
            ? `Копировать выбранные (${selectedLinkIds.length})`
            : 'Копировать выбранные'
        "
        styling-mode="outlined"
        :disabled="!selectedLinkIds.length || copyingSelected"
        hint="Создать копию каждой выбранной ссылки"
        @click="duplicateSelected"
      />
      <DxButton
        v-if="mainTabIndex === 0"
        icon="link"
        text="Скопировать фильтр"
        styling-mode="outlined"
        hint="Скопировать прямую ссылку с текущими фильтрами"
        @click="copyFilterLink"
      />
      <DxButton icon="refresh" text="Обновить" styling-mode="outlined" @click="refresh" />
    </template>

    <template #hint>
      Диапазон дат на вкладке «Ссылки» фильтрует по дате создания. Клики/uniques в колонках —
      за всё время. На вкладке «Targets» и в карточке ссылки диапазон фильтрует клики.
    </template>

    <template #toolbar>
      <div class="filters-row">
        <div class="filter-field">
          <label>{{ mainTabIndex === 0 ? 'Дата создания' : 'Период кликов' }}</label>
          <DateRangePicker v-model:from="rangeFrom" v-model:to="rangeTo" @change="onRangeChange" />
        </div>
        <div v-show="mainTabIndex === 0" class="filter-field">
          <label>Домен</label>
          <DxSelectBox
            :value="listFilters.domain_id"
            :items="domains"
            value-expr="id"
            display-expr="name"
            :show-clear-button="true"
            placeholder="Все"
            width="200"
            @value-changed="onDomainFilterChanged"
          />
        </div>
        <div v-show="mainTabIndex === 0" class="filter-field">
          <label>Группа</label>
          <DxSelectBox
            :value="listFilters.group_id"
            :items="groups"
            value-expr="id"
            display-expr="name"
            :show-clear-button="true"
            placeholder="Все"
            width="200"
            @value-changed="onGroupFilterChanged"
          />
        </div>
      </div>
      <div v-show="mainTabIndex === 0" class="filters-row filters-row--search">
        <div class="filter-field filter-field--search">
          <label>Поиск</label>
          <DxTextBox
            :value="listFilters.q"
            placeholder="Имя, code или tracking URL"
            :show-clear-button="true"
            width="100%"
            @value-changed="onQFilterChanged"
          />
        </div>
      </div>
    </template>

    <DxTabPanel
      class="links-tabs"
      height="100%"
      :selected-index="mainTabIndex"
      :defer-rendering="false"
      @selection-changed="onTabChanged"
    >
      <DxItem title="Ссылки">
        <div class="tab-body">
          <DxDataGrid
            ref="linksGrid"
            :data-source="linksStore"
            :remote-operations="{ paging: true, sorting: false, filtering: false }"
            v-bind="trackerGridProps"
            :allow-column-reordering="true"
            :column-resizing-mode="'widget'"
            :paging="paging"
            :pager="pager"
            height="100%"
            width="100%"
            @selection-changed="onLinksSelectionChanged"
            @row-dbl-click="(e: any) => goLinkDetail(e.data)"
          >
            <DxToolbar>
              <DxGridToolbarItem name="columnChooserButton" locate-in-menu="auto" />
            </DxToolbar>
            <DxColumnChooser :enabled="true" mode="select" title="Столбцы" />
            <DxStateStoring :enabled="true" type="localStorage" :storage-key="GRID_STATE_KEY" />
            <DxSelection mode="multiple" show-check-boxes-mode="always" />
            <DxScrolling mode="standard" />

            <DxColumn
              data-field="name"
              caption="Имя"
              css-class="truncate name-cell"
              width="350"
              :calculate-cell-value="(r: TrackerLink) => r.name || '—'"
            />
            <DxColumn data-field="domain_name" caption="Домен" css-class="mono" />
            <DxColumn
              data-field="tracker_node_ip"
              caption="Edge IP"
              css-class="mono"
              width="140"
              :calculate-cell-value="(r: TrackerLink) => r.tracker_node_ip || r.tracker_node_name || '—'"
            />
            <DxColumn name="code" caption="Code" cell-template="codeCell" width="120" />
            <DxColumn
              name="tracking_url"
              caption="Tracking URL"
              css-class="mono tracking-url-cell"
              cell-template="trackingUrlCell"
              :calculate-cell-value="(r: TrackerLink) => fullTrackingUrl(r) || '—'"
            />
            <DxColumn
              data-field="group_name"
              caption="Группа"
              :calculate-cell-value="(r: TrackerLink) => r.group_name || '—'"
            />
            <DxColumn
              data-field="target_url"
              caption="Исходная ссылка"
              css-class="mono truncate"
              width="220"
            />

            <DxColumn data-field="status" caption="Статус" width="110" cell-template="statusCell" />
            <DxColumn data-field="challenge_mode" caption="Challenge" width="100" />
            <DxColumn
              data-field="expires_at"
              caption="Истекает"
              width="160"
              :calculate-cell-value="(r: TrackerLink) => formatDateTime(r.expires_at)"
            />
            <DxColumn
              name="clicks"
              caption="Клики"
              width="80"
              :calculate-cell-value="(r: TrackerLink) => statsOf(r.id, 'clicks')"
            />
            <DxColumn
              name="unique_clicks"
              caption="Uniques"
              width="90"
              :calculate-cell-value="(r: TrackerLink) => statsOf(r.id, 'unique_clicks')"
            />
            <DxColumn
              name="bots"
              caption="Bots"
              width="70"
              :calculate-cell-value="(r: TrackerLink) => statsOf(r.id, 'bots')"
            />

            <DxColumn
              type="buttons"
              name="actions"
              :width="140"
              caption="Действия"
              :allow-reordering="false"
              :allow-hiding="false"
              :fixed="true"
              fixed-position="right"
            >
              <DxGridButton hint="Скопировать URL" icon="copy" :on-click="(e: any) => copyUrl(e.row.data)" />
              <DxGridButton
                hint="Создать копию"
                icon="plus"
                :on-click="(e: any) => duplicateLink(e.row.data)"
              />
              <DxGridButton hint="Изменить" icon="edit" :on-click="(e: any) => openEdit(e.row.data)" />
              <DxGridButton hint="Удалить" icon="trash" :on-click="(e: any) => removeLink(e.row.data)" />
            </DxColumn>

            <template #codeCell="{ data }">
              <a
                class="mono link"
                :href="linkDetailHref(data.data)"
                target="_blank"
                rel="noopener noreferrer"
                @click.stop
              >{{ data.data.code }}</a>
            </template>
            <template #trackingUrlCell="{ data }">
              <template v-for="url in [fullTrackingUrl(data.data)]" :key="url || 'empty'">
                <a
                  v-if="url"
                  class="mono link"
                  :href="url"
                  :title="url"
                  target="_blank"
                  rel="noopener noreferrer"
                  @click.stop
                >{{ url }}</a>
                <span v-else>—</span>
              </template>
            </template>
            <template #statusCell="{ data }">
              <StatusBadge :status="data.data.status" />
            </template>
          </DxDataGrid>
        </div>
      </DxItem>

      <DxItem title="Targets">
        <div class="tab-body">
          <DxDataGrid
            ref="targetsGrid"
            :data-source="targetsStore"
            :remote-operations="{ paging: true, sorting: false, filtering: false }"
            v-bind="trackerGridProps"
            :paging="paging"
            :pager="pager"
            height="100%"
            width="100%"
          >
            <DxScrolling mode="standard" />

            <DxColumn caption="Target URL" cell-template="targetCell" />
            <DxColumn data-field="link_count" caption="Ссылок" width="100" />
            <DxColumn data-field="clicks" caption="Клики" width="90" />
            <DxColumn data-field="unique_clicks" caption="Uniques" width="90" />
            <DxColumn data-field="bots" caption="Bots" width="80" />

            <template #targetCell="{ data }">
              <a
                class="mono link truncate-wide"
                href="#"
                :title="data.data.target_url"
                @click.prevent="goTargetDetail(data.data)"
              >
                {{ data.data.target_url }}
              </a>
            </template>
          </DxDataGrid>
        </div>
      </DxItem>
    </DxTabPanel>

    <template #overlays>
    <DxLoadPanel
      :visible="copyingSelected"
      :show-indicator="true"
      :show-pane="true"
      :shading="true"
      :message="copyProgressMessage"
    />
    <DxPopup
      v-model:visible="showModal"
      :title="editing ? 'Редактировать ссылку' : 'Создать ссылку'"
      :width="560"
      :height="'auto'"
      :show-close-button="!saving"
      @hiding="onPopupHiding"
    >
      <template #content>
        <div v-if="formError" class="form-error">{{ formError }}</div>
        <div class="form-grid">
          <div class="form-field">
            <label>Домен</label>
            <DxSelectBox
              v-model:value="form.domain_id"
              :items="domainSelectItems"
              value-expr="id"
              display-expr="label"
              placeholder="Выберите домен"
              :disabled="saving || bulkDone"
              width="100%"
            />
          </div>
          <div class="form-field">
            <label>Группа</label>
            <DxSelectBox
              v-model:value="form.group_id"
              :items="groups"
              value-expr="id"
              display-expr="name"
              placeholder="Выберите группу"
              :disabled="saving || bulkDone"
              width="100%"
            />
          </div>
          <div class="form-field">
            <label>Имя (опционально)</label>
            <DxTextBox
              v-model:value="form.name"
              placeholder="Название ссылки"
              :disabled="saving || bulkDone"
              width="100%"
            />
          </div>
          <div v-if="editing" class="form-field">
            <label>Исходная ссылка</label>
            <DxTextBox
              v-model:value="form.target_url"
              placeholder="https://offer.example/land"
              :disabled="saving"
              width="100%"
            />
          </div>
          <div v-else class="form-field">
            <label>Исходные ссылки</label>
            <DxTextArea
              v-model:value="form.target_url"
              :height="140"
              :placeholder="bulkTargetPlaceholder"
              :disabled="saving || bulkDone"
              width="100%"
            />
            <p class="form-field__hint">
              По одной URL на строку. Несколько строк — массовое создание (отдельный запрос на каждую).
              <template v-if="isBulkCreate"> Сейчас: {{ createTargetUrls.length }} шт.</template>
            </p>
          </div>
          <div v-if="editing || !isBulkCreate" class="form-field">
            <label>Code</label>
            <DxTextBox
              v-model:value="form.code"
              :placeholder="editing ? 'код ссылки' : 'сгенерирован, можно изменить'"
              :disabled="saving || bulkDone"
              width="100%"
            />
          </div>
          <div class="form-field">
            <label>Статус</label>
            <DxSelectBox
              v-model:value="form.status"
              :items="statusItems"
              :disabled="saving || bulkDone"
              width="100%"
            />
          </div>
          <div class="form-field">
            <label>Истекает</label>
            <DxDateBox
              v-model:value="form.expires_at"
              type="datetime"
              display-format="dd.MM.yyyy HH:mm"
              width="100%"
              :show-clear-button="true"
              placeholder="пусто = без срока"
              :disabled="saving || bulkDone"
            />
          </div>
          <div class="form-field">
            <label>Bot policy</label>
            <DxSelectBox
              v-model:value="form.bot_policy_id"
              :items="botPolicyItems"
              value-expr="id"
              display-expr="name"
              :show-clear-button="true"
              placeholder="Системный default"
              :disabled="saving || bulkDone"
              width="100%"
            />
            <p class="form-field__hint">
              Пусто — системный default из Настройки → Bot policies.
            </p>
          </div>
          <div class="form-field">
            <DxCheckBox
              v-model:value="form.challenge_on"
              text="JS challenge перед редиректом"
              :disabled="saving || bulkDone"
            />
            <p class="form-field__hint">
              Перед переходом на исходную ссылку посетителю показывается JS-проверка (антибот).
            </p>
          </div>
        </div>

        <div v-if="bulkProgress" class="bulk-progress">
          <div class="bulk-progress__title">
            Создание: {{ bulkProgress.current }} / {{ bulkProgress.total }}
          </div>
          <ul v-if="bulkResults.length" class="bulk-results">
            <li
              v-for="(r, idx) in bulkResults"
              :key="`${idx}-${r.url}`"
              :class="r.ok ? 'bulk-results__ok' : 'bulk-results__fail'"
            >
              <template v-if="r.ok">✓ {{ r.url }} → {{ r.trackingUrl || '—' }}</template>
              <template v-else>✗ {{ r.url }}: {{ r.error }}</template>
            </li>
          </ul>
        </div>

        <div v-if="bulkDone && createdLinksText" class="bulk-created">
          <label>Новые tracking URL</label>
          <DxTextArea :value="createdLinksText" :height="120" :read-only="true" width="100%" />
          <DxButton
            class="bulk-created__copy"
            text="Скопировать"
            icon="copy"
            styling-mode="outlined"
            @click="copyCreatedLinks"
          />
        </div>
      </template>
      <DxToolbarItem
        widget="dxButton"
        toolbar="bottom"
        location="after"
        :options="{
          text: bulkDone ? 'Закрыть' : 'Отмена',
          disabled: saving,
          onClick: closeModal,
        }"
      />
      <DxToolbarItem
        v-if="!bulkDone"
        widget="dxButton"
        toolbar="bottom"
        location="after"
        :options="{
          text: saving
            ? isBulkCreate
              ? 'Создание…'
              : 'Сохранение…'
            : isBulkCreate
              ? `Создать (${createTargetUrls.length})`
              : 'Сохранить',
          type: 'default',
          disabled: saving,
          onClick: save,
        }"
      />
    </DxPopup>
    </template>
  </TrackerListLayout>
</template>

<style scoped>
.filters-row {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  align-items: flex-end;
}

.filters-row--search {
  flex: 1 0 100%;
  width: 100%;
}

.filter-field {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.filter-field--search {
  flex: 1 1 320px;
  max-width: 560px;
}

.filter-field label {
  font-size: 12px;
  color: var(--dx-color-text-secondary, #666);
}

.links-tabs :deep(.dx-tabpanel-container),
.links-tabs :deep(.dx-multiview-wrapper),
.links-tabs :deep(.dx-multiview-item-container),
.links-tabs :deep(.dx-multiview-item),
.links-tabs :deep(.dx-multiview-item-content) {
  height: 100% !important;
  min-height: 0;
}

.tab-body {
  height: 100%;
  min-height: 0;
  padding-top: 8px;
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.tab-body > :deep(.dx-datagrid) {
  flex: 1 1 auto;
  min-height: 0;
  width: 100%;
}

.link {
  color: var(--dx-color-primary, #1976d2);
  text-decoration: none;
}

.link:hover {
  text-decoration: underline;
}

.form-error {
  margin-bottom: 12px;
  padding: 8px 12px;
  border-radius: 4px;
  background: #fdecea;
  color: #c62828;
  font-size: 0.875rem;
}

.form-grid {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.form-field label {
  display: block;
  margin-bottom: 4px;
  font-size: 12px;
  color: var(--dx-color-text-secondary, #666);
}

.form-field__hint {
  margin: 6px 0 0;
  font-size: 12px;
  line-height: 1.4;
  color: var(--dx-color-text-secondary, #666);
}

.bulk-progress {
  margin-top: 16px;
  padding-top: 12px;
  border-top: 1px solid var(--dx-color-border, #ddd);
}

.bulk-progress__title {
  font-size: 13px;
  font-weight: 600;
  margin-bottom: 8px;
}

.bulk-results {
  list-style: none;
  margin: 0;
  padding: 0;
  max-height: 180px;
  overflow: auto;
  font-size: 12px;
  font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace;
}

.bulk-results li {
  padding: 4px 0;
  word-break: break-all;
}

.bulk-results__ok {
  color: var(--dx-color-success, #2e7d32);
}

.bulk-results__fail {
  color: var(--dx-color-danger, #c62828);
}

.bulk-created {
  margin-top: 12px;
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.bulk-created label {
  font-size: 12px;
  color: var(--dx-color-text-secondary, #666);
}

.bulk-created__copy {
  align-self: flex-start;
}

:deep(.mono) {
  font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace;
  font-size: 0.9em;
}

:deep(.truncate),
.truncate-wide {
  max-width: 280px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  display: inline-block;
  vertical-align: bottom;
}

:deep(.name-cell.truncate) {
  max-width: 330px;
}

.truncate-wide {
  max-width: 480px;
}

:deep(.tracking-url-cell),
:deep(.tracking-url-cell .dx-datagrid-text-content) {
  overflow: visible;
  text-overflow: clip;
  white-space: nowrap;
}
</style>
